import { memo, useState, useEffect, useCallback } from 'react';
import { isEmpty } from 'lodash';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useFormik } from 'formik';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useToaster from 'hooks/UseToaster';
//Import Components
import { Alert, Button, ExtendedInput, SkeletonLoader } from '@geneui/components';
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import ResetSave from 'components/common/ResetSave';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Services
import { ProviderHttpService } from 'services/http';
// Import Constants
import { l, AlertTypes, CommonIcons, ModalsClassNames } from 'constants/common';

const { success } = AlertTypes;

const TelegramSettingsSection = () => {
    const { t } = useTranslation();

    const { doPostRequest } = useRequest();
    const { showToaster } = useToaster();

    const [isTelegramSettingsLoading, setIsTelegramSettingsLoading] = useState(false);
    const [telegramBotUserName, setTelegramBotUserName] = useState('');
    const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
    const [isDisconnectModalLoading, setIsDisconnectModalLoading] = useState(false);

    const formik = useFormik({
        initialValues: {
            apiKey: '',
        },
        validationSchema: Yup.object({
            apiKey: Yup.string().required(t(l.CannotBeEmpty, { fieldName: t(l.TelegramApiKey) })),
        }),
        onSubmit: (values, formik) => {
            return doPostRequest(saveTelegramRequest.request, {
                requestBody: { apiKey: values.apiKey },
                successCallback: (data) => {
                    formik.resetForm({
                        values: {
                            apiKey: values.apiKey,
                        },
                    });
                    setTelegramBotUserName(data?.UserName ?? '');
                    showToaster(success, t(l.TelegramSettingsSavedSuccessfully));
                },
            });
        },
    });

    const { values, errors, dirty, isSubmitting, isValid, setFieldValue, submitForm, resetForm } = formik;

    const { getTelegramRequest, saveTelegramRequest, deleteTelegramRequest } = useCancelableRequests({
        getTelegramRequest: ProviderHttpService.getTelegram,
        saveTelegramRequest: ProviderHttpService.saveTelegram,
        deleteTelegramRequest: ProviderHttpService.deleteTelegram,
    });

    const getTelegramSettings = () => {
        setIsTelegramSettingsLoading(true);
        doPostRequest(getTelegramRequest.request, {
            requestBody: {},
            successCallback: (data) => {
                resetForm({
                    values: { apiKey: data?.ApiKey ?? '' },
                });
                setTelegramBotUserName(data?.UserName ?? '');
            },
        }).then(() => {
            setIsTelegramSettingsLoading(false);
        });
    };

    const onDisconnect = useCallback(() => setIsDisconnectModalOpen(true), []);
    const onCancelDisconnectModal = useCallback(() => setIsDisconnectModalOpen(false), []);

    const removeSettings = useCallback(() => {
        setIsDisconnectModalLoading(true);

        doPostRequest(deleteTelegramRequest.request, {
            requestBody: {},
            successCallback: () => {
                resetForm({
                    values: { apiKey: '' },
                });
                setTelegramBotUserName('');
                setIsDisconnectModalOpen(false);

                showToaster(success, t(l.TelegramSettingsDisconnectedSuccessfully));
            },
        }).then(() => {
            setIsDisconnectModalLoading(false);
        });
    }, [doPostRequest, deleteTelegramRequest.request, resetForm, showToaster, t]);

    const telegramApiKeyInputChangeHandler = (e) => {
        setFieldValue('apiKey', e.target.value);
    };

    const init = () => {
        getTelegramSettings();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <>
            <SectionWithTitle
                title={t(l.TelegramSettings)}
                className="crm-telegram-provider-settings-wrapper"
                contentClassName="crm-telegram-provider-settings-content"
            >
                <Alert
                    closable={false}
                    title={
                        <Trans
                            i18nKey={l.TelegramDescription}
                            components={{
                                1: (
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    <a
                                        href="https://knowledge.betconstruct.com/en_US/telegram-bots-how-to-articles/how-to-connect-igaming-bot-to-crm"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                ),
                                2: (
                                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                                    <a
                                        href="https://core.telegram.org/bots#how-do-i-create-a-bot"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    />
                                ),
                                3: <p></p>,
                            }}
                        />
                    }
                    iconProps={{ type: 'bc-icon-info' }}
                />
                <div className="fields-btn-group">
                    <div className="input-container">
                        <SkeletonLoader isBusy={isTelegramSettingsLoading}>
                            <ExtendedInput
                                value={telegramBotUserName}
                                disabled={isSubmitting}
                                readOnly={true}
                                label={t(l.TelegramBotId)}
                                labelAppearance="swap"
                            />
                        </SkeletonLoader>
                    </div>

                    <div className="input-container">
                        <SkeletonLoader isBusy={isTelegramSettingsLoading}>
                            <ExtendedInput
                                value={values.apiKey}
                                onChange={telegramApiKeyInputChangeHandler}
                                disabled={isSubmitting}
                                placeholder={t(l.TelegramApiKey)}
                                isValid={!errors.apiKey}
                                errorText={errors.apiKey}
                                type="password"
                            />
                        </SkeletonLoader>
                    </div>
                    <div className="telegram-settings-content-action-buttons-container">
                        <Button
                            appearance="outline"
                            onClick={onDisconnect}
                            disabled={isEmpty(values.apiKey) || isTelegramSettingsLoading}
                        >
                            {t(l.Disconnect)}
                        </Button>
                        <ResetSave
                            hasAdditionalSettings={false}
                            resetButtonClickHandler={resetForm}
                            disableSave={!isValid || !dirty || isSubmitting}
                            disableReset={!dirty || isSubmitting}
                            saveButtonClickHandler={submitForm}
                            isLoadingSave={isSubmitting}
                        />
                    </div>
                </div>
            </SectionWithTitle>
            {isDisconnectModalOpen && (
                <ConfirmationModal
                    onOk={removeSettings}
                    onCancel={onCancelDisconnectModal}
                    isVisibleModal={isDisconnectModalOpen}
                    titleText={t(l.DisconnectTelegram)}
                    questionLabel={t(l.DisconnectTelegramDescription)}
                    iconType={CommonIcons.Info}
                    actionLabel={t(l.Confirm)}
                    className={ModalsClassNames.UsedIn}
                    isLoading={isDisconnectModalLoading}
                />
            )}
        </>
    );
};

export default memo(TelegramSettingsSection);
